import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import {
  Help as HelpIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterIcon,
  ZoomIn as ZoomIcon,
  AccountTree as GraphIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Inventory as EvidenceIcon
} from '@mui/icons-material';

interface RelationshipGraphTutorialProps {
  open: boolean;
  onClose: () => void;
}

const RelationshipGraphTutorial: React.FC<RelationshipGraphTutorialProps> = ({
  open,
  onClose
}) => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      label: 'Understanding the Graph',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            What is a Relationship Graph?
          </Typography>
          <Typography paragraph>
            The Relationship Graph visualizes connections between people, locations, and evidence in your investigation. 
            It helps you identify patterns, connections, and relationships that might not be obvious in other views.
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Node Types:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#4CAF50' }} />
                </ListItemIcon>
                <ListItemText primary="People (Green)" secondary="Victims, suspects, witnesses, investigators" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#2196F3' }} />
                </ListItemIcon>
                <ListItemText primary="Locations (Blue)" secondary="Crime scenes, residences, businesses, transit points" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#FF9800' }} />
                </ListItemIcon>
                <ListItemText primary="Evidence (Orange)" secondary="Physical evidence, documents, digital records" />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Relationship Types:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ width: 20, height: 2, backgroundColor: '#4CAF50' }} />
                </ListItemIcon>
                <ListItemText primary="Co-location (Green)" secondary="People who were at the same place at the same time" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ width: 20, height: 2, backgroundColor: '#2196F3' }} />
                </ListItemIcon>
                <ListItemText primary="Movement (Blue)" secondary="How people moved between locations" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ width: 20, height: 2, backgroundColor: '#FF9800' }} />
                </ListItemIcon>
                <ListItemText primary="Evidence-Location (Orange)" secondary="Where evidence was found" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Box sx={{ width: 20, height: 2, backgroundColor: '#9C27B0' }} />
                </ListItemIcon>
                <ListItemText primary="Family (Purple)" secondary="Family relationships between people" />
              </ListItem>
            </List>
          </Box>
        </Box>
      )
    },
    {
      label: 'Basic Navigation',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            How to Navigate the Graph
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Zoom Controls:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><ZoomIcon /></ListItemIcon>
                <ListItemText 
                  primary="Zoom In/Out" 
                  secondary="Use the zoom buttons to focus on specific areas or get an overview" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><GraphIcon /></ListItemIcon>
                <ListItemText 
                  primary="Center View" 
                  secondary="Reset the view to center all nodes" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Interactive Features:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><InfoIcon /></ListItemIcon>
                <ListItemText 
                  primary="Hover Over Nodes" 
                  secondary="See detailed information and highlight connected relationships" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Click Nodes" 
                  secondary="Open detailed information panel for that entity" 
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Pro Tip:</strong> Hover over a node to see all its connections highlighted. 
              This helps you quickly understand who/what is related to that entity.
            </Typography>
          </Alert>
        </Box>
      )
    },
    {
      label: 'Filtering and Simplification',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Managing Graph Complexity
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Graph Type Filter:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText 
                  primary="People Only" 
                  secondary="Focus on relationships between people only" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocationIcon /></ListItemIcon>
                <ListItemText 
                  primary="Locations Only" 
                  secondary="See how locations are connected through movements" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><EvidenceIcon /></ListItemIcon>
                <ListItemText 
                  primary="Evidence Only" 
                  secondary="Focus on evidence and where it was found" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Relationship Type Filter:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><FilterIcon /></ListItemIcon>
                <ListItemText 
                  primary="Co-location" 
                  secondary="See who was together at the same time and place" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><TrendingUpIcon /></ListItemIcon>
                <ListItemText 
                  primary="Movement" 
                  secondary="Track how people moved between locations" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><EvidenceIcon /></ListItemIcon>
                <ListItemText 
                  primary="Evidence-Location" 
                  secondary="See where evidence was discovered" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Simplify Graph Feature:</strong>
            </Typography>
            <Typography paragraph>
              When the graph becomes too crowded, use the "Simplify Graph" option:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Enable Simplify Graph" 
                  secondary="Reduces complexity by filtering out less connected nodes" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><TrendingUpIcon /></ListItemIcon>
                <ListItemText 
                  primary="Adjust Min Connections" 
                  secondary="Only show nodes with at least this many connections" 
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Simplification removes less important connections to focus on the most significant relationships. 
              Use this when you want to see the "big picture" without getting overwhelmed by details.
            </Typography>
          </Alert>
        </Box>
      )
    },
    {
      label: 'Investigation Workflows',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Common Investigation Scenarios
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Scenario 1: Identifying Key Witnesses</strong>
            </Typography>
            <Typography paragraph>
              <strong>Steps:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="1. Set Graph Type to 'People Only'" 
                  secondary="Focus on relationships between people" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="2. Set Relationship Type to 'Co-location'" 
                  secondary="See who was present at crime scenes" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="3. Look for highly connected nodes" 
                  secondary="People with many connections are likely key witnesses" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Scenario 2: Tracking Suspect Movement</strong>
            </Typography>
            <Typography paragraph>
              <strong>Steps:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="1. Set Graph Type to 'All Entities'" 
                  secondary="Include people and locations" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="2. Set Relationship Type to 'Movement'" 
                  secondary="Focus on movement patterns" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="3. Follow the blue lines" 
                  secondary="Trace how suspects moved between locations" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Scenario 3: Evidence Analysis</strong>
            </Typography>
            <Typography paragraph>
              <strong>Steps:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="1. Set Graph Type to 'Evidence Only'" 
                  secondary="Focus on evidence items" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="2. Set Relationship Type to 'Evidence-Location'" 
                  secondary="See where evidence was found" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="3. Look for evidence clusters" 
                  secondary="Multiple evidence items in one location may indicate a key scene" 
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Pro Tip:</strong> Use the time range slider in the left panel to focus on specific time periods. 
              This helps you see relationships that occurred during critical moments in your investigation.
            </Typography>
          </Alert>
        </Box>
      )
    },
    {
      label: 'Advanced Techniques',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Advanced Analysis Techniques
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Finding Hidden Connections:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><TrendingUpIcon /></ListItemIcon>
                <ListItemText 
                  primary="Use Simplify Graph with Min Connections = 2" 
                  secondary="Shows only people with multiple connections" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText 
                  primary="Look for 'bridge' nodes" 
                  secondary="People who connect different groups or locations" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Timeline Analysis:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Adjust time range slider" 
                  secondary="Focus on specific time periods" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Compare different time periods" 
                  secondary="See how relationships change over time" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Pattern Recognition:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><GraphIcon /></ListItemIcon>
                <ListItemText 
                  primary="Look for clusters" 
                  secondary="Groups of closely connected nodes indicate important relationships" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><TrendingUpIcon /></ListItemIcon>
                <ListItemText 
                  primary="Identify central figures" 
                  secondary="Nodes with many connections are often key to the investigation" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Cross-Reference with Other Views:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Use Map View for spatial context" 
                  secondary="See geographic relationships" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Use Timeline View for temporal context" 
                  secondary="Understand the sequence of events" 
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Remember:</strong> The Relationship Graph is most powerful when used in combination with other views. 
              Use it to identify patterns, then dive deeper with Map, Timeline, and Table views for detailed analysis.
            </Typography>
          </Alert>
        </Box>
      )
    }
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon color="primary" />
          <Typography variant="h6">Relationship Graph Tutorial</Typography>
        </Box>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Stepper */}
          <Box sx={{ width: 300, borderRight: 1, borderColor: 'divider', p: 2 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>
                    <Typography variant="subtitle2">{step.label}</Typography>
                  </StepLabel>
                  <StepContent>
                    <Box sx={{ mb: 2 }}>
                      <div>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          sx={{ mt: 1, mr: 1 }}
                          size="small"
                        >
                          {index === steps.length - 1 ? 'Finish' : 'Continue'}
                        </Button>
                        <Button
                          disabled={index === 0}
                          onClick={handleBack}
                          sx={{ mt: 1, mr: 1 }}
                          size="small"
                        >
                          Back
                        </Button>
                      </div>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            {activeStep === steps.length && (
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Tutorial Complete!
                </Typography>
                <Typography paragraph>
                  You now know how to effectively use the Relationship Graph for your investigations.
                </Typography>
                <Button onClick={handleReset} variant="outlined">
                  Restart Tutorial
                </Button>
              </Box>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
            {activeStep < steps.length && (
              <Box>
                {steps[activeStep].content}
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                  >
                    {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RelationshipGraphTutorial;
